import { ConfigService } from './../config/config.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  map,
  throwError,
  catchError,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BackendResponse } from '../../types/interfaces';
import { Category } from '../../../shared/models/category.model';
import { MappingService } from '../utils';
import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../utils/snackbar.service';
@Injectable({
  providedIn: 'root',
})
export class CategoriesService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService
  ) {}

  orderedCategories: Array<string> = [];
  categories: Array<Category>;
  private viewCategories$ = new Subject<Category[]>();
  private shopCategorySelect$ = new Subject<Category>();
  private hideAllCategories$ = new BehaviorSubject<boolean>(false);

  private selectedCategory = new Subject();
  private orderedCategories$: Subject<Array<string>> = new Subject();

  getCategories(): Observable<BackendResponse<Category[]>> {
    const url = this.configService.endpoints.categories.getCategories;
    return this.http.get<BackendResponse<Category[]>>(url).pipe(
      map((arg: any) => deserializeResponse<Category[]>(arg, Category)),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant(
            'categories.error.inCategoriesRetrieving'
          )
        );
        return throwError(() => error);
      })
    );
  }

  getCategory(categoryId: number): Observable<BackendResponse<Category>> {
    const url = replaceUrlParams(
      this.configService.endpoints.categories.getCategory,
      ['categoryId'],
      [categoryId]
    );
    return this.http.get<BackendResponse<Category>>(url).pipe(
      map((arg: any) => deserializeResponse<Category>(arg, Category)),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('categories.error.inCategoryRetrieving')
        );
        return throwError(() => error);
      })
    );
  }

  checkIfCategoryAlreadyExist(category: string): boolean {
    return this.orderedCategories.indexOf(category) !== -1;
  }

  setViewCategories(value: Category[]) {
    this.viewCategories$.next(value);
  }

  getViewCategories() {
    return this.viewCategories$.asObservable();
  }

  setHideAllCategories(value: boolean) {
    this.hideAllCategories$.next(value);
  }

  getHideAllCategories() {
    return this.hideAllCategories$.asObservable();
  }

  setShopCategorySelect(value: Category) {
    this.shopCategorySelect$.next(value);
  }

  getShopCategorySelect() {
    return this.shopCategorySelect$.asObservable();
  }

  addOrderedCategory(category: string): void {
    if (!this.checkIfCategoryAlreadyExist(category)) {
      this.orderedCategories.push(category);
      this.orderedCategories$.next(this.orderedCategories);
    }
  }

  deleteCategory(category: string): void {
    for (let i = 0; i < this.orderedCategories.length; i++) {
      if (this.orderedCategories[i] === category) {
        this.orderedCategories.splice(i, 1);
        this.orderedCategories$.next(this.orderedCategories);
        break;
      }
    }
  }

  deleteAllOrderedCategories() {
    this.orderedCategories = [];
    this.orderedCategories$.next(this.orderedCategories);
  }

  getOrderedCategories(): Observable<Array<string>> {
    return this.orderedCategories$.asObservable();
  }

  setSelectedCategory(id: number) {
    this.selectedCategory.next(id);
  }

  getSelectedCategory() {
    return this.selectedCategory.asObservable();
  }
}
